import React, { useEffect, useMemo, useState } from 'react';
import { BiSolidBriefcase } from 'react-icons/bi';
import { MdAccountCircle } from 'react-icons/md';
import { RiBankFill, RiFileListFill } from 'react-icons/ri';
import { VscChecklist } from 'react-icons/vsc';
import { useSelector } from 'react-redux';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { HEADERMENU_PATH } from '../../../routes/RoutesURL';
import { RootState } from '../../../store/rootReducer';
import AccessDenied from '../../AccessDenied';
import NoMatch from '../../NoMatch';
import { KYCAIResubmission } from '../../common/Header/Header';
import { InvestorStatus } from '../../login/utils/Status';
import OnboardingRightContainer from '../onboarding/components/common/Container';
import OnboardingMainContainer from '../onboarding/components/common/MainContainer';
import UserAlret from '../userAlret';
import SettingAccreditation from './fragments/Accreditation';
import Agreements from './fragments/Agreements';
import BankDetails from './fragments/BankDetails';
import KYC from './fragments/DefaultKYC';
import SettingInvestmentPreferences from './fragments/InvestmentPreferences';
import Profile from './fragments/Profile';

export enum SETTINGS_PATH {
    PROFILE = '',
    KYC = 'kyc',
    AGREEMENTS = 'agreements',
    BANKDETAILS = 'bankdetails',
    INVESTMENTPREFERENCES = 'investment-preferences',
    NOTIFICATIONS = 'notifications',
    ACCREDITATION = 'accreditation',
    // TEAMMANAGEMENT = 'team-management',
}

const tabs = [
    {
        index: 0,
        name: 'Profile',
        icon: <MdAccountCircle />,
        path: SETTINGS_PATH.PROFILE,
    },
    {
        index: 1,
        name: 'Investment Preferences',
        icon: <VscChecklist />,
        path: SETTINGS_PATH.INVESTMENTPREFERENCES,
    },
    {
        index: 5,
        name: 'Accreditation',
        icon: <BiSolidBriefcase />,
        path: SETTINGS_PATH.ACCREDITATION,
    },
    {
        index: 2,
        name: 'KYC',
        icon: <BiSolidBriefcase />,
        path: SETTINGS_PATH.KYC,
    },
    {
        index: 3,
        name: 'Agreements',
        icon: <RiFileListFill />,
        path: SETTINGS_PATH.AGREEMENTS,
    },
    {
        index: 4,
        name: 'Bank Details',
        icon: <RiBankFill />,
        path: SETTINGS_PATH.BANKDETAILS,
    },
    // {
    //     index: 6,
    //     name: 'Team Management',
    //     icon: <RiBankFill />,
    //     path: SETTINGS_PATH.TEAMMANAGEMENT,
    // },
];

const SettingsWrapper: React.FC = () => {
    const location = useLocation();

    const investorType = useSelector((state: RootState) => state.user.investor?.investorType);
    const investor = useSelector((state: RootState) => state.user.investor);
    const isAccessDenied = useMemo(() => {
        const teamManagementRestriction = investorType !== 'Corporate Accredited' && investorType !== 'Institutional';
        const accreditationRestriction = investorType === 'Institutional';
        return {
            teamManagementRestriction,
            accreditationRestriction,
        };
    }, [investorType]);
    const [currentPath, setCurrentPath] = useState('');

    /**Get current Path */
    useEffect(() => {
        setCurrentPath(location.pathname);
    }, [location]);

    return (
        <OnboardingMainContainer
            leftSide={<SettingSidebar />}
            isAccount={true}
            rightSide={
                <div className="setting_page">
                    <UserAlret />
                    <OnboardingRightContainer>
                        {investor?.status?.accreditation === InvestorStatus.PendingAIResubmission &&
                        currentPath.includes(SETTINGS_PATH.ACCREDITATION) ? (
                            <KYCAIResubmission type="Accreditation" />
                        ) : null}
                        {investor?.status?.kyc === InvestorStatus.PendingKYCResubmission &&
                        currentPath.includes(SETTINGS_PATH.KYC) ? (
                            <KYCAIResubmission type="KYC" />
                        ) : null}
                    </OnboardingRightContainer>
                    <Routes>
                        {/** KYC */}
                        <Route path={SETTINGS_PATH.KYC} element={<KYC />} />
                        {/** End KYC */}

                        {/** Investment Preferences */}
                        <Route path={SETTINGS_PATH.INVESTMENTPREFERENCES} element={<SettingInvestmentPreferences />} />
                        {/** End Investment Preferences */}

                        {/** Agreements */}
                        <Route path={SETTINGS_PATH.AGREEMENTS} element={<Agreements />} />
                        {/** End Agreements */}

                        {/** Bank details */}
                        <Route path={SETTINGS_PATH.BANKDETAILS} element={<BankDetails />} />
                        {/** End Bank details */}

                        {/** Profile */}
                        <Route path={SETTINGS_PATH.PROFILE} element={<Profile />} />

                        {/** Accreditation*/}
                        <Route
                            path={SETTINGS_PATH.ACCREDITATION}
                            element={
                                isAccessDenied.accreditationRestriction ? <AccessDenied /> : <SettingAccreditation />
                            }
                        />
                        {/* <Route
                            path={SETTINGS_PATH.TEAMMANAGEMENT}
                            element={isAccessDenied.teamManagementRestriction ? <AccessDenied /> : <TeamManagement />}
                        /> */}
                        <Route path="*" element={<NoMatch notShowBtn={true} />} />
                        {/** End Profile */}
                    </Routes>
                </div>
            }
            id="onboarding"
        />
    );
};

export default SettingsWrapper;

const SettingSidebar: React.FC = () => {
    const navigate = useNavigate();
    const [tabNumber, setTab] = useState<number | null>(null);
    const location = useLocation();
    const investorType = useSelector((state: RootState) => state.user.investor?.investorType);
    const tabList = useMemo(() => {
        let filteredTabs = [...tabs];

        // if (investorType !== 'Corporate Accredited' && investorType !== 'Institutional') {
        //     filteredTabs = filteredTabs.filter((t) => t.path !== SETTINGS_PATH.TEAMMANAGEMENT);
        // }

        if (investorType === 'Institutional') {
            filteredTabs = filteredTabs.filter((t) => t.path !== SETTINGS_PATH.ACCREDITATION);
        }

        return filteredTabs;
    }, [investorType]);

    useEffect(() => {
        switch (location.pathname) {
            case '/settings':
                setTab(0);
                break;
            case `${HEADERMENU_PATH.settings}/${SETTINGS_PATH.PROFILE}`:
                setTab(0);
                break;
            case `${HEADERMENU_PATH.settings}/${SETTINGS_PATH.INVESTMENTPREFERENCES}`:
                setTab(1);
                break;
            case `${HEADERMENU_PATH.settings}/${SETTINGS_PATH.KYC}`:
                setTab(2);
                break;
            case `${HEADERMENU_PATH.settings}/${SETTINGS_PATH.AGREEMENTS}`:
                setTab(3);
                break;
            case `${HEADERMENU_PATH.settings}/${SETTINGS_PATH.BANKDETAILS}`:
                setTab(4);
                break;
            case `${HEADERMENU_PATH.settings}/${SETTINGS_PATH.ACCREDITATION}`:
                setTab(5);
                break;
            // case `${HEADERMENU_PATH.settings}/${SETTINGS_PATH.TEAMMANAGEMENT}`:
            //     setTab(6);
            // break;
            default:
        }
    }, [location]);

    return (
        <div className="h-full overflow-y-auto" id="banner">
            <div className="relative">
                <div className="absolute w-full hidden md:block">
                    <div className="flex justify-center items-center font-[WorkSans-Semibold] pt-2 pb-4 mb-0 w-full h-40 font-semibold text-[#FFFFFF] text-center sm:text-center md:text-left text-3xl bg-[#20222B] uppercase">
                        Account <br /> Settings
                    </div>
                    <div className="pl-3 md:pl-10 py-6">
                        {tabList.map((tab) => (
                            <div key={tab.index} className="flex flex-col">
                                <div
                                    className={`flex items-center py-4 cursor-pointer pl-3 ${
                                        tabNumber === tab.index ? 'bg-[#142C53]' : ''
                                    }`}
                                    onClick={() =>
                                        navigate(
                                            tab.path === ''
                                                ? `${HEADERMENU_PATH.settings}`
                                                : `${HEADERMENU_PATH.settings}/${tab.path}`,
                                        )
                                    }
                                >
                                    {/* <div className="text-2xl mr-3 text-[#26AE60]" >
                                        {tab.icon}
                                    </div> */}
                                    <p
                                        className={`${
                                            tabNumber === tab.index ? 'text-[#FFFFFF]' : 'text-[#5C5C5C]'
                                        } WorkSans text-base font-bold`}
                                    >
                                        {tab.name}
                                    </p>
                                    {/* {tabNumber === tab.index && (
                                        <div className="absolute bg-[#41D17E] w-1.5 rounded-lg h-10 -right-0.5" />
                                    )} */}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            <div className="flex w-full  flex-col md:hidden">
                <div className="py-6 px-3 w-full text-left font-[WorkSans-Semibold] font-bold text-lg uppercase text-white bg-[#20222B]">
                    Account Settings
                </div>
                <div className="overflow-auto flex no-scrollbar">
                    <div className="flex m-auto">
                        {tabs.map((tab) => (
                            <div key={tab.index} className="flex relative">
                                <div
                                    className={`flex flex-col justify-center items-center py-4 px-2 cursor-pointer w-max ${
                                        tabNumber === tab.index ? 'bg-[#142C53]' : ''
                                    }`}
                                    onClick={() =>
                                        navigate(
                                            tab.path === ''
                                                ? `${HEADERMENU_PATH.settings}`
                                                : `${HEADERMENU_PATH.settings}/${tab.path}`,
                                        )
                                    }
                                    id={tab.name}
                                >
                                    {/* <div className="text-3xl mb-2" style={{ color: '#C3996C' }}>
                                        {tab.icon}
                                    </div> */}
                                    <p
                                        className={`${
                                            tabNumber === tab.index ? 'text-white' : 'text-[#5C5C5C]'
                                        } font-[WorkSans-Semibold] font-bold text-sm min-w-[5rem] text-center`}
                                    >
                                        {tab.name}
                                    </p>
                                    {/* {tabNumber === tab.index && (
                                        <div className="bg-gray-700 h-1.5 w-full rounded-lg absolute bottom-0" />
                                    )} */}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};
